import type {CampoEditable, CatalogosDeSeguimiento, ExportacionResumen} from '@cnc/contracts';
import {useRef, useState} from 'react';
import {tr} from '../i18n/idioma';
import {aTexto, CAMPO_POR_CLAVE, convertirValor, valorDeFila, valorVistoDeFila} from './campos-exportacion';
import {EntradaDeCampo} from './entrada-de-campo';

type Props = {
  fila: ExportacionResumen;
  campo: CampoEditable;
  // Como se ve la celda cuando no se esta editando.
  texto: string;
  catalogos: CatalogosDeSeguimiento | undefined;
  // `visto`: el valor que la fila mostraba (para detectar cambios de otra persona).
  onGuardar: (codigo: string, campo: CampoEditable, valor: unknown, visto: unknown) => Promise<void>;
};

// Una celda de la tabla en modo de edicion rapida: se hace clic, se escribe
// y con Enter (o al salir) se guarda ese solo campo. Esc cancela.
export function CeldaEditable({fila, campo, texto, catalogos, onGuardar}: Props) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [estado, setEstado] = useState<'quieta' | 'guardando' | 'guardada' | 'error'>('quieta');
  const [mensaje, setMensaje] = useState<string | null>(null);
  // Enter o Esc cierran la celda y el navegador puede disparar ademas "salir
  // del campo": solo la primera accion cuenta.
  const abierta = useRef(false);
  const definicion = CAMPO_POR_CLAVE.get(campo);
  if (!definicion) return <>{texto}</>;

  const abrir = () => {
    setValor(aTexto(valorDeFila(fila, campo)));
    setMensaje(null);
    abierta.current = true;
    setEditando(true);
  };
  const cancelar = () => {
    abierta.current = false;
    setEditando(false);
  };
  const guardar = async () => {
    if (!abierta.current) return;
    abierta.current = false;
    const inicial = aTexto(valorDeFila(fila, campo));
    setEditando(false);
    if (valor === inicial) return;
    const convertido = convertirValor(campo, valor);
    if ('error' in convertido) {
      setEstado('error');
      setMensaje(convertido.error);
      return;
    }
    setEstado('guardando');
    try {
      await onGuardar(fila.codigo, campo, convertido.valor, valorVistoDeFila(fila, campo));
      setEstado('guardada');
      setMensaje(null);
    } catch (error) {
      setEstado('error');
      setMensaje(error instanceof Error ? error.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };

  if (editando) {
    return (
      <EntradaDeCampo
        campo={definicion}
        valor={valor}
        catalogos={catalogos}
        onCambiar={setValor}
        compacta
        autoFoco
        onTecla={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void guardar();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelar();
          }
        }}
        onSalir={() => void guardar()}
      />
    );
  }
  const tono =
    estado === 'error'
      ? 'border-alerta text-alerta'
      : estado === 'guardada'
        ? 'border-verde'
        : estado === 'guardando'
          ? 'border-linea opacity-60'
          : 'border-transparent hover:border-verde/60';
  return (
    <button
      type="button"
      onClick={abrir}
      title={mensaje ?? tr('Clic para editar', 'Click to edit')}
      className={`block h-7 w-full cursor-text truncate rounded border border-dashed px-1.5 text-left ${tono}`}
    >
      {estado === 'guardando' ? '…' : texto}
      {estado === 'error' && ' ⚠'}
    </button>
  );
}
